"use client";

// Shared i18n machinery for both surfaces. The dictionaries themselves stay
// per-vertical (apps ship their own lib/i18n/{en,ja}.ts) and are injected via
// registerDictionaries() at module load + the I18nProvider `dictionaries` prop.
// Both the React path (useTranslation) and the module-level getTranslations()
// used by the API transport read from the same registry.
//
// Everything except *how `lang` is discovered* is common to admin and liff, so
// the provider body lives in I18nProviderWithLang and each surface supplies the
// lang it resolved. They resolve it differently on purpose: admin defers to an
// effect (see I18nProvider below), while liff reads useSearchParams during
// render so an ?lang=en visitor never sees a frame of Japanese first. Collapsing
// them onto one strategy would regress whichever surface lost its own.
import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "../router";

type Dictionary = Record<string, string>;

let dictionaries: Record<string, Dictionary> = {};

/** Register the vertical's dictionaries. Call once at app module load. */
export function registerDictionaries(dicts: Record<string, Dictionary>) {
  dictionaries = dicts;
}

interface I18nContextValue {
  lang: string;
  t: (key: string, params?: Record<string, string>) => string;
  langHref: (path: string) => string;
  /** A booking status in the viewer's language; the raw status if unmapped. */
  statusLabel: (status: string) => string;
  /**
   * Switch language and remember the choice. Supplied by the admin provider
   * only; on liff it is a no-op, because that surface takes its language from
   * the ?lang= it was linked with and has nothing to persist it to.
   */
  setLang: (lang: string) => void;
}

const I18nContext = createContext<I18nContextValue>({
  lang: "ja",
  t: (key) => key,
  langHref: (p) => p,
  statusLabel: (s) => s,
  setLang: () => {},
});

export function useTranslation() {
  return useContext(I18nContext);
}

const statusKeyMap: Record<string, string> = {
  CONFIRMED: "statusConfirmed",
  CANCELLED: "statusCancelled",
  COMPLETED: "statusCompleted",
};

export function getTranslations(lang?: string) {
  const l = lang ?? "ja";
  const dict = dictionaries[l] ?? dictionaries.ja ?? {};
  return (key: string, params?: Record<string, string>): string => {
    let value = dict[key] ?? key;
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        value = value.replace(`{${k}}`, v);
      }
    }
    return value;
  };
}

export function useLangRouter() {
  const navigate = useNavigate();
  const { lang } = useTranslation();

  const push = useCallback(
    (href: string) => {
      navigate(lang === "en" ? appendLang(href) : href);
    },
    [navigate, lang],
  );

  const replace = useCallback(
    (href: string) => {
      navigate(lang === "en" ? appendLang(href) : href, { replace: true });
    },
    [navigate, lang],
  );

  // `back` used to be re-exported here and was never called anywhere — the one
  // "back" in the tree is window.history.back() in UserDetailPage. Dropped
  // rather than carried through the seam, where it would have been a promise
  // about history semantics that this adapter does not need to keep.
  return useMemo(() => ({ push, replace }), [push, replace]);
}

function appendLang(href: string): string {
  const [path, search] = href.split("?", 2);
  if (search) {
    const params = new URLSearchParams(search);
    params.set("lang", "en");
    return `${path}?${params.toString()}`;
  }
  return `${href}?lang=en`;
}

const LANG_STORAGE_KEY = "booking.admin.lang";

/*
  Persistence is localStorage, deliberately, and only for admin.

  The alternative was a column on the dashboard account, which would follow a
  person between browsers. It would also put a language preference behind a
  network round trip on every first paint, and admin already resolves lang
  synchronously so that AdminLayout's mount-time redirect captures the right
  one (see I18nProvider). A per-browser choice is what a toggle in a topbar
  reads as anyway.

  Every access is wrapped: Safari private mode and "block all cookies" both
  make localStorage *throw* on read, not return null, and an admin that cannot
  render because storage is disabled is a worse bug than a forgotten toggle.
*/
function readStoredLang(): string | null {
  try {
    const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
    return stored === "en" || stored === "ja" ? stored : null;
  } catch {
    return null;
  }
}

function storeLang(lang: string) {
  try {
    window.localStorage.setItem(LANG_STORAGE_KEY, lang);
  } catch {
    /* Storage unavailable: the toggle still works for this page's lifetime. */
  }
}

/**
 * An EXPLICIT ?lang= wins over the stored choice, so a link someone was sent
 * still opens in the language it promises. Only when the query says nothing at
 * all does the remembered preference apply.
 */
function resolveLang(search: string): string {
  const param = new URLSearchParams(search).get("lang");
  if (param !== null) return param === "en" ? "en" : "ja";
  return readStoredLang() ?? "ja";
}

/**
 * The provider body, for a `lang` the caller already resolved.
 *
 * Exported so the liff surface can supply a lang read from useSearchParams
 * without duplicating any of this.
 */
export function I18nProviderWithLang({
  lang,
  children,
  dictionaries: dicts,
  setLang,
}: {
  lang: string;
  children: ReactNode;
  dictionaries?: Record<string, Dictionary>;
  /** Omitted by liff, which has no toggle; defaults to a no-op. */
  setLang?: (lang: string) => void;
}) {
  // Allow passing dictionaries via prop as well as the module registry, so a
  // provider placed above any import that registered them still works.
  if (dicts) dictionaries = dicts;

  const langHref = useCallback(
    (path: string) => (lang === "en" ? appendLang(path) : path),
    [lang],
  );

  const t = useMemo(
    () =>
      (key: string, params?: Record<string, string>): string => {
        const dict = dictionaries[lang] ?? dictionaries.ja ?? {};
        let value = dict[key] ?? key;
        if (params) {
          for (const [k, v] of Object.entries(params)) {
            value = value.replace(`{${k}}`, v);
          }
        }
        return value;
      },
    [lang],
  );

  const statusLabel = useCallback(
    (status: string) => {
      const dict = dictionaries[lang] ?? dictionaries.ja ?? {};
      const key = statusKeyMap[status];
      return key ? (dict[key] ?? status) : status;
    },
    [lang],
  );

  const noopSetLang = useCallback(() => {}, []);

  return (
    <I18nContext.Provider
      value={{ lang, t, langHref, statusLabel, setLang: setLang ?? noopSetLang }}
    >
      {children}
    </I18nContext.Provider>
  );
}

export function I18nProvider({
  children,
  dictionaries: dicts,
}: {
  children: ReactNode;
  dictionaries?: Record<string, Dictionary>;
}) {
  /*
    READ SYNCHRONOUSLY. There is no SSR left to mismatch.

    This used to seed "ja" and resolve the real locale in a post-mount effect,
    because reading window.location during the initial render diverged from the
    server render and produced hydration errors when ?lang=en was present. Every
    app is a client-only Vite SPA now, so there is no server render to agree
    with and the constraint is gone. resolveLang also consults the persisted
    admin choice, which is only safe here for the same reason: nothing
    server-rendered has to agree with it.

    THE DEFERRAL WAS NOT FREE. AdminLayout redirects unauthenticated users in an
    effect with [] deps, which captures useLangRouter at MOUNT — when lang was
    still "ja". So /dashboard?lang=en redirected to /login with the query
    dropped, and the login screen came back in Japanese. Resolving the locale
    before first paint removes the window entirely rather than making the
    redirect defensive about it.
  */
  const [lang, setLangState] = useState(() =>
    typeof window === "undefined" ? "ja" : resolveLang(window.location.search),
  );

  /*
    Still watched after mount: a client-side navigation changes the query
    without remounting this provider, and the initialiser above only runs once.
    Same precedence as the initialiser -- going back to a URL that carries no
    ?lang= must land on the remembered choice, not silently on ja.
  */
  useEffect(() => {
    const sync = () => setLangState(resolveLang(window.location.search));
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, []);

  const navigate = useNavigate();

  const setLang = useCallback(
    (next: string) => {
      const value = next === "en" ? "en" : "ja";
      storeLang(value);
      setLangState(value);

      /*
        Rewrite ?lang= to match, rather than relying on the store alone.

        langHref builds every in-app link out of `lang`, so a stale query would
        have the next click argue with the toggle. It also keeps the URL
        shareable, and keeps a plain reload on the chosen language through the
        higher-precedence branch of resolveLang instead of depending on storage
        that private mode may refuse.

        replace, not push: a language switch is not a place in history to go
        back to, and pushing one would make Back appear to do nothing.
      */
      const params = new URLSearchParams(window.location.search);
      if (value === "en") params.set("lang", "en");
      else params.delete("lang");
      const qs = params.toString();
      navigate(`${window.location.pathname}${qs ? `?${qs}` : ""}`, {
        replace: true,
      });
    },
    [navigate],
  );

  return (
    <I18nProviderWithLang lang={lang} dictionaries={dicts} setLang={setLang}>
      {children}
    </I18nProviderWithLang>
  );
}
